import React, {useContext} from 'react';
import {Chip, Stack, Typography} from '@mui/material';
import ImageNotAvailablePlaceholder from '../ImageNotAvailablePlaceholder';
import InstantApprovalChip from './InstantApprovalChip';
import RequestSpaceChip from './RequestSpaceChip';
import LocationListItemImages from './LocationImages/LocationListItemImages';
import './style.css'
import {AiOutlineLeft, AiOutlineRight} from 'react-icons/ai'
import NotLiveLocationChip from "../../NotLiveLocationChip";
import LocationFavoriteIcon from '../LocationFavoriteIcon';
import {AppContext} from "../../../context/app-context";
import {isBuyerAccountType} from '../../../util/AccountUtils';
import Money from 'js-money';
import type {Supplier} from '../../../types/Supplier';
import styled from "@mui/system/styled";
import {useImageCarousel} from "../../../hooks/location/useImageCarousel";
import {getLocationGallery} from "../../../util/LocationMediaUtils";

interface LocationListItemMapPreviewProps {
  location: Supplier;
  showVisibilityChip: boolean;
  isFavorite?: boolean;
  onContentClick?: () => void;
}

const MapPreviewCard = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'white',
  overflow: 'hidden',
  margin: 0,
  padding: 0,
  border: '1px solid #E9E9FB',
  borderRadius: '8px',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    borderColor: '#3F51B5',
  },
}));

const ImageContainer = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  paddingTop: '62%',
  overflow: 'hidden',
  backgroundColor: '#f4f4f8',
}));

const ImageInner = styled('div')(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  '& > div': {
    width: '100%',
    height: '100%',
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}));

const ContentSection = styled('div')(() => ({
  padding: '12px 12px 16px 12px',
  cursor: 'pointer',
}));

const HeaderRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
}));

const TitleRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
}));

const Title = styled(Typography)(() => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  fontFamily: 'Inter',
  color: '#000',
}));

const FeaturesSection = styled('div')(() => ({
  marginBottom: '8px',
}));

const PricingSection = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginTop: '16px',
  paddingTop: '12px',
  borderTop: '1px solid #E0E0E0',
}));

const PriceBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const PriceAmount = styled(Typography)(() => ({
  fontSize: '1.75rem',
  fontWeight: 600,
  fontFamily: 'Inter',
  color: '#000',
}));

const PriceUnit = styled('span')(() => ({
  fontSize: '1rem',
  fontWeight: 400,
}));

const NavButton = styled('button')(() => ({
  width: '28px',
  height: '28px',
  borderRadius: '50%',
  border: 'none',
  outline: 'none',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  cursor: 'pointer',
  zIndex: 2,
  '&:disabled': {
    backgroundColor: 'rgba(165, 164, 164, 0.7)',
    cursor: 'not-allowed',
  },
  '&:hover:not(:disabled)': {
    backgroundColor: 'white',
  },
}));

const LeftButton = styled(NavButton)(() => ({
  left: '12px',
}));

const RightButton = styled(NavButton)(() => ({
  right: '12px',
}));

const LocationListItemMapPreview: React.FC<LocationListItemMapPreviewProps> = (props) => {
  const appContext = useContext(AppContext);
  const {location, showVisibilityChip, isFavorite, onContentClick} = props;
  const listingImage = location.listingImageFileName;
  const gallery = getLocationGallery(location);

  // Total images = listing image (if exists) + gallery images
  const totalImages = (listingImage ? 1 : 0) + gallery.length;

  const {activeSlide, prevImage: prevImageBase, nextImage: nextImageBase} = useImageCarousel({totalImages});

  // Map preview needs stopPropagation in addition to preventDefault
  const prevImage = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    prevImageBase(e);
  };

  const nextImage = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    nextImageBase(e);
  };

  const {locationName, city, state} = location;
  const locationDescription = locationName || `${city}, ${state}`;

  // Calculate pricing
  const currency = Money.USD;
  const pricePerMonth = location.pricePerMonth || 0;
  const pricePerDay = location.pricePerDay || 0;
  const moneyPricePerMonth = new Money(pricePerMonth, currency);
  const moneyPricePerDay = new Money(pricePerDay, currency);

  const formattedMonthly = moneyPricePerMonth.isPositive() ? `${currency.symbol}${moneyPricePerMonth}` : '';
  const formattedDaily = moneyPricePerDay.isPositive() ? `${currency.symbol}${moneyPricePerDay}` : '';

  // Get features - show first 3
  const features = location.features || [];
  const displayFeatures = features.slice(0, 3);
  const remainingCount = features.length - displayFeatures.length;


  return (
    <MapPreviewCard>
      {/* Image Section */}
      <ImageContainer>
        <ImageInner>
          {
            listingImage ?
              <LocationListItemImages
                errorimg='/app-images/not-found-image/place_holder_image.png'
                placeholderimg="/app-images/loading/image-loader.gif"
                activeSlide={activeSlide}
                listingImageFileName={listingImage}
                gallery={gallery}
                locationId={location.locationId}
                height="100%"
                width="100%"
                borderRadius="0"
                objectfit="cover"
              /> :
              <ImageNotAvailablePlaceholder
                height="100%"
                width="100%"
                borderRadius="0"
              />
          }
          {totalImages > 1 && (
            <>
              <LeftButton
                disabled={activeSlide === 0}
                onClick={prevImage}
              >
                <AiOutlineLeft size={14} color={activeSlide === 0 ? "#999" : "#000"}/>
              </LeftButton>
              <RightButton
                disabled={activeSlide === totalImages - 1}
                onClick={nextImage}
              >
                <AiOutlineRight size={14} color={activeSlide === totalImages - 1 ? "#999" : "#000"}/>
              </RightButton>
            </>
          )}
        </ImageInner>
      </ImageContainer>

      {/* Content Section */}
      <ContentSection onClick={onContentClick}>
        {/* Approval Status */}
        <HeaderRow>
          <div>
            {location.hasInstantApproval ? (
              <InstantApprovalChip/>
            ) : (
              <RequestSpaceChip/>
            )}
            {showVisibilityChip && !location.visible && <NotLiveLocationChip/>}
          </div>
        </HeaderRow>

        {/* Title & Favorite */}
        <TitleRow>
          <Title>{locationDescription}</Title>
          {isBuyerAccountType(appContext?.user?.type) && (
            <LocationFavoriteIcon locationId={location.locationId} isFavorite={isFavorite}/>
          )}
        </TitleRow>

        {/* Features */}
        <FeaturesSection>
          <Stack direction="row" sx={{flexWrap: 'wrap', gap: 0.5}}>
            {displayFeatures.map((f) => (
              <Chip
                key={f}
                label={f}
                size="small"
                variant="outlined"
                sx={{fontSize: '0.72rem', borderColor: '#E9E9FB', backgroundColor: '#F5F5FB', height: 22}}
              />
            ))}
            {remainingCount > 0 && (
              <Chip
                label={`+${remainingCount} more`}
                size="small"
                variant="outlined"
                sx={{fontSize: '0.72rem', borderColor: '#E9E9FB', backgroundColor: '#F5F5FB', height: 22}}
              />
            )}
          </Stack>
        </FeaturesSection>

        {/* Pricing */}
        <PricingSection>
          <PriceBlock>
            <PriceAmount>
              {formattedMonthly || formattedDaily}
              <PriceUnit>
                /{formattedMonthly ? 'mon' : 'day'}
              </PriceUnit>
            </PriceAmount>
          </PriceBlock>
          <PriceBlock style={{textAlign: 'right'}}>
            {formattedMonthly && formattedDaily && (
              <PriceAmount>
                {formattedDaily}
                <PriceUnit>/day</PriceUnit>
              </PriceAmount>
            )}
          </PriceBlock>
        </PricingSection>
      </ContentSection>
    </MapPreviewCard>
  );
};

export default LocationListItemMapPreview;
